import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Input, Popconfirm, Segmented, Spin, Tag, Tooltip, Typography, message, Alert } from 'antd'
import type { InputRef } from 'antd/es/input'
import {
  BulbOutlined,
  CommentOutlined,
  DeleteOutlined,
  GlobalOutlined,
  PlusOutlined,
  RobotOutlined,
  SendOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { streamChatMessage, type ChatStreamEvent, type HarnessRoundLog } from '../api/chatStream'
import { api, getAdminToken, PAGE_SIZE } from '../api'
import { KNOWLEDGE_SCOPE_OPTIONS, scopeQuery, type KnowledgeScope } from '../constants/knowledgeScope'
import { KnowledgeFilePreviewDrawer } from '../components/KnowledgeFilePreviewDrawer'
import {
  ChatAnswerWithCitations,
  hasCitationMarkers,
  type CitationRef,
} from '../components/ChatAnswerWithCitations'
import { ChatMarkdown } from '../components/ChatMarkdown'
import { ChatWebSources, type StreamPhase, type WebRef } from '../components/ChatMessageExtras'
import { ChatAgentSteps } from '../components/ChatAgentSteps'
import {
  applyAgentStepEvent,
  hasRunningAgentStep,
  shouldShowAgentIntro,
  type AgentStep,
  type McpUiConfig,
} from '../lib/agentSteps'
import { fetchChatComposerPrefs, persistChatComposerPrefs } from '../lib/chatComposerPrefs'
import {
  applyDatasetMention,
  detectDatasetMentionLabel,
  filterDatasetMentions,
  findActiveMention,
  parseDatasetMention,
  type ChatDatasetOption,
} from '../lib/chatDatasetMention'
import {
  convLiveCache,
  emptyConvLive,
  mergeConvMessages,
  streamAbortCache,
  type ConvLiveState,
} from '../lib/chatConvCache'
import { ChatRagDebugPanel, type RagDebugPayload } from '../components/ChatRagDebugPanel'
import { ChatAnswerFigures, type AnswerFigureRef } from '../components/ChatAnswerFigures'
import {
  ChatMessageActions,
  isPersistedChatMessageId,
  type QaCaseLabel,
} from '../components/ChatMessageActions'
import { WorkspaceAside, WorkspaceShell, formatDate, PageLoading } from '../components/ui'

const SUGGESTIONS = [
  '报销需要哪些材料？',
  '产品上线有哪些检查项？',
  '数据权限管理原则是什么？',
]

type Conv = { id: number; title: string; updated_at: string }

type DocRef = {
  item_id: number
  title?: string
  scope?: KnowledgeScope
  created_by?: string | null
  created_at?: string | null
  rank?: number
  score?: number
}

type PreviewTarget = { itemId: number; scope: KnowledgeScope }

type Msg = {
  id: number
  role: string
  content: string
  created_at: string
  qa_case_label?: string
  qa_gold_item_ids?: number[]
  chunk_refs?: DocRef[] | null
  answer_figures?: AnswerFigureRef[] | null
  thinking_content?: string | null
  web_refs?: WebRef[] | null
  citation_refs?: CitationRef[] | null
  harness_rounds?: HarnessRoundLog[] | null
  agent_steps?: AgentStep[] | null
}

const SCOPE_OPTIONS = KNOWLEDGE_SCOPE_OPTIONS

const CHAT_LAST_CONV_KEY = 'ai-kb:chat:last-conv-id'
/** 距底部在此范围内视为「贴底」，流式更新时继续自动滚屏。 */
const CHAT_SCROLL_STICK_PX = 96

/** 用于判断是否需要自动滚底；不含 qa_case_label，避免点赞/点踩触发滚屏。 */
function chatAutoScrollSignature(
  msgs: Msg[],
  sending: boolean,
  streamingId: number | null,
): string {
  const last = msgs[msgs.length - 1]
  const lastStep = last?.agent_steps?.[last.agent_steps.length - 1]
  return [
    msgs.length,
    sending ? 1 : 0,
    streamingId ?? '',
    last?.id ?? '',
    last?.content?.length ?? 0,
    last?.thinking_content?.length ?? 0,
    last?.agent_steps?.length ?? 0,
    lastStep?.detail?.length ?? 0,
    lastStep?.status ?? '',
  ].join('\0')
}

function ChatSources({
  refs,
  defaultScope,
  onPreview,
  messageId,
  goldItemIds = [],
  onGoldChange,
}: {
  refs: DocRef[]
  defaultScope: KnowledgeScope
  onPreview: (target: PreviewTarget) => void
  messageId?: number
  goldItemIds?: number[]
  onGoldChange?: (ids: number[]) => void
}) {
  const [goldBusy, setGoldBusy] = useState(false)
  const canMarkGold = messageId != null && isPersistedChatMessageId(messageId) && onGoldChange

  const toggleGold = (itemId: number) => {
    if (!canMarkGold || goldBusy) return
    const next = goldItemIds.includes(itemId)
      ? goldItemIds.filter((id) => id !== itemId)
      : [...goldItemIds, itemId]
    setGoldBusy(true)
    api
      .patch<{ gold_item_ids: number[]; case_label: string }>(
        `/public/chat/messages/${messageId}/source-gold`,
        { gold_item_ids: next },
      )
      .then((r) => {
        onGoldChange!(r.data.gold_item_ids)
        if (r.data.gold_item_ids.length) {
          message.success('已标注准确来源，并收录为 Good Case')
        } else {
          message.success('已取消准确来源标注')
        }
      })
      .catch((e) => message.error(e.message || '标注失败'))
      .finally(() => setGoldBusy(false))
  }

  if (!refs.length) return null
  return (
    <div className="chat-msg-sources">
      <div className="chat-msg-sources-title">
        参考来源（{refs.length} 篇文档）
        {canMarkGold ? (
          <span className="chat-msg-sources-hint"> · 点击「准确」标注期望命中的文档</span>
        ) : null}
      </div>
      <ul className="chat-msg-sources-list">
        {refs.map((r, idx) => {
          const title = r.title?.trim() || `文档 #${r.item_id}`
          const creator = r.created_by?.trim()
          const createdAt = r.created_at ? formatDate(r.created_at) : ''
          const scope = r.scope === 'personal' || r.scope === 'enterprise' ? r.scope : defaultScope
          const rank = r.rank ?? idx + 1
          const score = typeof r.score === 'number' && r.score > 0 ? r.score : null
          const isGold = goldItemIds.includes(r.item_id)
          return (
            <li
              key={r.item_id}
              className={`chat-msg-sources-item${isGold ? ' is-gold' : ''}`}
            >
              <div className="chat-msg-sources-row">
                <span className="chat-msg-sources-rank">#{rank}</span>
                <div className="chat-msg-sources-doc-wrap">
                  <Tooltip title={title} placement="topLeft">
                    <Typography.Link
                      className="chat-msg-sources-doc"
                      onClick={() => onPreview({ itemId: r.item_id, scope })}
                    >
                      {title}
                    </Typography.Link>
                  </Tooltip>
                </div>
                {score != null ? (
                  <span className="chat-msg-sources-score">相关度 {score.toFixed(2)}</span>
                ) : null}
                {canMarkGold ? (
                  <button
                    type="button"
                    className={`chat-msg-sources-gold-btn${isGold ? ' is-active' : ''}`}
                    disabled={goldBusy}
                    onClick={() => toggleGold(r.item_id)}
                  >
                    {isGold ? '已标准确' : '标为准确'}
                  </button>
                ) : isGold ? (
                  <Tag color="gold" style={{ margin: 0 }}>
                    准确
                  </Tag>
                ) : null}
              </div>
              <div className="chat-msg-sources-meta">
                <span>创建人：{creator || '未记录'}</span>
                {createdAt ? <span>创建时间：{createdAt}</span> : null}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default function Chat() {
  const navigate = useNavigate()
  const [kbScope, setKbScope] = useState<KnowledgeScope>('enterprise')
  const [datasetOptions, setDatasetOptions] = useState<ChatDatasetOption[]>([])
  const [mentionOpen, setMentionOpen] = useState(false)
  const [mentionStart, setMentionStart] = useState(0)
  const [mentionQuery, setMentionQuery] = useState('')
  const [mentionIndex, setMentionIndex] = useState(0)
  const [inputCursor, setInputCursor] = useState(0)
  const [previewTarget, setPreviewTarget] = useState<PreviewTarget | null>(null)
  const [convs, setConvs] = useState<Conv[]>([])
  const [convTotal, setConvTotal] = useState(0)
  const [convLoadingMore, setConvLoadingMore] = useState(false)
  const asideListRef = useRef<HTMLDivElement>(null)
  const convLoadMoreRef = useRef<HTMLDivElement>(null)
  const convPageRef = useRef(1)
  const convLoadingMoreRef = useRef(false)
  const hasMoreConvsRef = useRef(false)
  const [curId, setCurId] = useState<number | null>(null)
  const [messages, setMessages] = useState<Msg[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [ragDebugOn, setRagDebugOn] = useState(false)
  const [lastRagDebug, setLastRagDebug] = useState<RagDebugPayload | null>(null)
  const [deepThink, setDeepThink] = useState(false)
  const [webSearch, setWebSearch] = useState(false)
  const [streamingId, setStreamingId] = useState<number | null>(null)
  const [streamPhase, setStreamPhase] = useState<StreamPhase>('done')
  const [mcpUi, setMcpUi] = useState<McpUiConfig>({})
  const [esStatus, setEsStatus] = useState<{
    enabled: boolean
    connected: boolean
    url?: string
  } | null>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLElement | null>(null)
  const stickToBottomRef = useRef(true)
  const scrollSigRef = useRef('')
  const composerRef = useRef<InputRef>(null)
  const curIdRef = useRef<number | null>(null)
  const loadSeqRef = useRef(0)
  const liveStateRef = useRef<ConvLiveState>(emptyConvLive())

  curIdRef.current = curId
  liveStateRef.current = { messages, sending, streamingId, streamPhase }

  /** setCurId 异步；切换对话前须同步更新 ref，否则 applyConvLive 会跳过 UI 更新。 */
  const setActiveConvId = useCallback((id: number | null) => {
    curIdRef.current = id
    setCurId(id)
  }, [])

  const prependConv = useCallback((conv: Conv) => {
    setConvs((prev) => (prev.some((c) => c.id === conv.id) ? prev : [conv, ...prev]))
  }, [])

  const resolveScrollContainer = useCallback(() => {
    if (!scrollContainerRef.current && bottomRef.current) {
      scrollContainerRef.current = bottomRef.current.closest('.workspace-scroll') as HTMLElement | null
    }
    return scrollContainerRef.current
  }, [])

  const scrollChatToBottom = useCallback(
    (behavior: ScrollBehavior = 'auto') => {
      const el = resolveScrollContainer()
      if (el) {
        el.scrollTo({ top: el.scrollHeight, behavior })
        return
      }
      bottomRef.current?.scrollIntoView({ behavior })
    },
    [resolveScrollContainer],
  )

  /** 发送新问题或切回对话时，强制滚到最新一条（即使用户此前上翻看过历史）。 */
  const scrollToLatest = useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      stickToBottomRef.current = true
      scrollSigRef.current = ''
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollChatToBottom(behavior)
        })
      })
    },
    [scrollChatToBottom],
  )

  const syncConvToUi = useCallback((cid: number) => {
    if (curIdRef.current !== cid) return
    const live = convLiveCache.get(cid) ?? emptyConvLive()
    setMessages(live.messages as Msg[])
    setSending(live.sending)
    setStreamingId(live.streamingId)
    setStreamPhase(live.streamPhase)
  }, [])

  const patchConvLive = useCallback(
    (
      cid: number,
      patch: Partial<ConvLiveState> | ((prev: ConvLiveState) => Partial<ConvLiveState>),
    ) => {
      const prev = convLiveCache.get(cid) ?? emptyConvLive()
      const delta = typeof patch === 'function' ? patch(prev) : patch
      convLiveCache.set(cid, { ...prev, ...delta })
      syncConvToUi(cid)
    },
    [syncConvToUi],
  )

  const abortConvStream = useCallback((cid: number) => {
    streamAbortCache.get(cid)?.abort()
    streamAbortCache.delete(cid)
  }, [])

  const persistCurrentConvLive = useCallback(() => {
    const prevId = curIdRef.current
    if (prevId == null) return
    convLiveCache.set(prevId, liveStateRef.current)
  }, [])

  const setMessageCaseLabel = useCallback(
    (msgId: number, label: QaCaseLabel) => {
      const cid = curIdRef.current
      if (cid == null) return
      patchConvLive(cid, (prev) => ({
        messages: prev.messages.map((m) =>
          m.id === msgId ? { ...m, qa_case_label: label } : m,
        ),
      }))
    },
    [patchConvLive],
  )

  const setMessageGoldIds = useCallback(
    (msgId: number, goldIds: number[]) => {
      const cid = curIdRef.current
      if (cid == null) return
      patchConvLive(cid, (prev) => ({
        messages: prev.messages.map((m) =>
          m.id === msgId
            ? {
                ...m,
                qa_gold_item_ids: goldIds,
                qa_case_label: goldIds.length ? 'good' : m.qa_case_label,
              }
            : m,
        ),
      }))
    },
    [patchConvLive],
  )

  const curConv = convs.find((c) => c.id === curId)
  const scopeLabel = SCOPE_OPTIONS.find((o) => o.value === kbScope)?.label ?? '知识库'
  const filteredMentionDatasets = useMemo(
    () => filterDatasetMentions(datasetOptions, mentionQuery),
    [datasetOptions, mentionQuery],
  )
  const activeDatasetLabel = useMemo(
    () => detectDatasetMentionLabel(input, datasetOptions),
    [input, datasetOptions],
  )
  const previewScopeParams = useMemo(
    () => scopeQuery(previewTarget?.scope ?? kbScope),
    [previewTarget?.scope, kbScope],
  )

  const openSourcePreview = useCallback((target: PreviewTarget) => {
    if (!getAdminToken()) {
      message.warning('预览原文需使用管理账号登录（与后台同一账号）')
      void navigate(`/manage/login?redirect=${encodeURIComponent('/chat')}`)
      return
    }
    setPreviewTarget(target)
  }, [navigate])

  const loadConvs = useCallback((page: number, append: boolean) => {
    if (append) {
      if (convLoadingMoreRef.current) return Promise.resolve()
      convLoadingMoreRef.current = true
      setConvLoadingMore(true)
    }
    return api
      .get<{ items: Conv[]; total: number; page: number }>('/public/chat/conversations', {
        params: { page, page_size: PAGE_SIZE },
      })
      .then((r) => {
        setConvTotal(r.data.total)
        convPageRef.current = r.data.page
        setConvs((prev) => {
          if (!append) return r.data.items
          const seen = new Set(prev.map((c) => c.id))
          const merged = [...prev]
          for (const c of r.data.items) {
            if (!seen.has(c.id)) merged.push(c)
          }
          return merged
        })
      })
      .catch(() => {})
      .finally(() => {
        if (append) {
          convLoadingMoreRef.current = false
          setConvLoadingMore(false)
        }
      })
  }, [])

  const hasMoreConvs = convs.length < convTotal
  hasMoreConvsRef.current = hasMoreConvs

  useEffect(() => {
    const root = asideListRef.current
    const sentinel = convLoadMoreRef.current
    if (!root || !sentinel || !hasMoreConvs) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return
        if (convLoadingMoreRef.current || !hasMoreConvsRef.current) return
        void loadConvs(convPageRef.current + 1, true)
      },
      { root, rootMargin: '48px', threshold: 0 },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMoreConvs, convs.length, convLoadingMore, loadConvs])

  useEffect(() => {
    setMentionOpen(false)
  }, [kbScope])

  useEffect(() => {
    api
      .get<{ items: { id: number; name: string }[] }>('/public/datasets/options', {
        params: { scope: kbScope },
      })
      .then((r) => setDatasetOptions(r.data.items || []))
      .catch(() => setDatasetOptions([]))
  }, [kbScope])

  const applyConvLive = useCallback(
    (id: number, state: ConvLiveState, opts?: { scrollToLatest?: boolean }) => {
      convLiveCache.set(id, state)
      if (curIdRef.current === id) {
        stickToBottomRef.current = true
        scrollSigRef.current = ''
        setMessages(state.messages as Msg[])
        setSending(state.sending)
        setStreamingId(state.streamingId)
        setStreamPhase(state.streamPhase)
        if (opts?.scrollToLatest !== false) {
          scrollToLatest('auto')
        }
      }
    },
    [scrollToLatest],
  )

  const loadConv = useCallback(
    (id: number, opts?: { background?: boolean }) => {
      const seq = ++loadSeqRef.current
      if (!opts?.background) setLoading(true)
      api
        .get<{ messages: Msg[] }>(`/public/chat/conversations/${id}`)
        .then((r) => {
          if (seq !== loadSeqRef.current) return
          const cached = convLiveCache.get(id)
          const msgs = mergeConvMessages(cached?.messages, r.data.messages) as Msg[]
          const state: ConvLiveState = {
            messages: msgs,
            sending: cached?.sending ?? false,
            streamingId: cached?.streamingId ?? null,
            streamPhase: cached?.streamPhase ?? 'done',
          }
          applyConvLive(id, state, { scrollToLatest: !opts?.background })
          const last = msgs[msgs.length - 1]
          if (last?.role === 'user' && !state.sending) {
            window.setTimeout(() => {
              if (curIdRef.current !== id || loadSeqRef.current !== seq) return
              const live = convLiveCache.get(id)
              if (live?.sending) return
              api
                .get<{ messages: Msg[] }>(`/public/chat/conversations/${id}`)
                .then((retry) => {
                  if (curIdRef.current !== id || loadSeqRef.current !== seq) return
                  const next = mergeConvMessages(
                    convLiveCache.get(id)?.messages,
                    retry.data.messages,
                  ) as Msg[]
                  const tail = next[next.length - 1]
                  if (tail?.role !== 'assistant') return
                  applyConvLive(
                    id,
                    {
                      messages: next,
                      sending: false,
                      streamingId: null,
                      streamPhase: 'done',
                    },
                    { scrollToLatest: false },
                  )
                })
                .catch(() => {})
            }, 2500)
          }
        })
        .catch((e) => {
          if (seq !== loadSeqRef.current) return
          message.error(e.message)
        })
        .finally(() => {
          if (seq !== loadSeqRef.current) return
          setLoading(false)
        })
    },
    [applyConvLive],
  )

  const selectConv = useCallback(
    (id: number) => {
      if (id === curIdRef.current) return
      persistCurrentConvLive()
      setActiveConvId(id)
      const live = convLiveCache.get(id)
      if (live) {
        loadSeqRef.current += 1
        setLoading(false)
        applyConvLive(id, live)
        if (!live.sending) loadConv(id, { background: true })
      } else {
        loadConv(id)
      }
    },
    [loadConv, persistCurrentConvLive, applyConvLive, setActiveConvId],
  )

  useEffect(() => {
    void loadConvs(1, false)
    const raw = sessionStorage.getItem(CHAT_LAST_CONV_KEY)
    if (raw) {
      const id = Number.parseInt(raw, 10)
      if (Number.isFinite(id) && id > 0) {
        setActiveConvId(id)
        const cached = convLiveCache.get(id)
        if (cached) applyConvLive(id, cached)
        loadConv(id, { background: Boolean(cached) })
      }
    }
    void fetchChatComposerPrefs().then((p) => {
      setDeepThink(p.deepThink)
      setWebSearch(p.webSearch)
    })
    api
      .get<{ enabled: boolean }>('/public/chat/rag-debug')
      .then((r) => setRagDebugOn(r.data.enabled))
      .catch(() => {})
    api
      .get<McpUiConfig>('/public/chat/features')
      .then((r) =>
        setMcpUi({
          mcp_enabled: r.data.mcp_enabled,
          mcp_use_builtin_kb: r.data.mcp_use_builtin_kb,
          mcp_display_label: r.data.mcp_display_label,
        }),
      )
      .catch(() => {})
    api
      .get<{ elasticsearch?: { enabled?: boolean; connected?: boolean; url?: string } }>('/health')
      .then((r) => {
        const es = r.data.elasticsearch
        if (es) {
          setEsStatus({
            enabled: Boolean(es.enabled),
            connected: Boolean(es.connected),
            url: es.url,
          })
        }
      })
      .catch(() => setEsStatus(null))
  }, [loadConvs, loadConv, applyConvLive, setActiveConvId])

  useEffect(() => {
    return () => {
      persistCurrentConvLive()
    }
  }, [persistCurrentConvLive])

  useEffect(() => {
    if (curId != null) sessionStorage.setItem(CHAT_LAST_CONV_KEY, String(curId))
  }, [curId])

  useEffect(() => {
    const el = bottomRef.current?.closest('.workspace-scroll') as HTMLElement | null
    scrollContainerRef.current = el
    if (!el) return
    const syncStick = () => {
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight
      stickToBottomRef.current = dist <= CHAT_SCROLL_STICK_PX
    }
    el.addEventListener('scroll', syncStick, { passive: true })
    return () => el.removeEventListener('scroll', syncStick)
  }, [curId, loading])

  useEffect(() => {
    const sig = chatAutoScrollSignature(messages, sending, streamingId)
    const contentChanged = sig !== scrollSigRef.current
    scrollSigRef.current = sig
    if (!(sending || streamingId != null || contentChanged)) return
    // 流式输出期间不自动滚屏，便于用户向上查看历史消息
    if (streamingId != null) return
    if (!stickToBottomRef.current) return
    requestAnimationFrame(() => {
      scrollChatToBottom('smooth')
    })
  }, [messages, sending, streamingId, scrollChatToBottom])

  const newChat = () => {
    persistCurrentConvLive()
    api
      .post<Conv>('/public/chat/conversations')
      .then((r) => {
        loadSeqRef.current += 1
        setActiveConvId(r.data.id)
        const state = emptyConvLive()
        convLiveCache.set(r.data.id, state)
        setMessages(state.messages as Msg[])
        setSending(false)
        setStreamingId(null)
        setStreamPhase('done')
        prependConv(r.data)
        void loadConvs(1, false)
      })
      .catch((e) => message.error(e.message))
  }

  const deleteConv = (id: number) => {
    abortConvStream(id)
    convLiveCache.delete(id)
    api
      .delete(`/public/chat/conversations/${id}`)
      .then(() => {
        message.success('已删除对话')
        if (curId === id) {
          sessionStorage.removeItem(CHAT_LAST_CONV_KEY)
          loadSeqRef.current += 1
          setActiveConvId(null)
          setMessages([])
          setSending(false)
          setStreamingId(null)
          setStreamPhase('done')
        }
        void loadConvs(1, false)
      })
      .catch((e) => message.error(e.message))
  }

  const pickDatasetMention = useCallback(
    (ds: ChatDatasetOption) => {
      const { text, cursor } = applyDatasetMention(input, mentionStart, inputCursor, ds.name)
      setInput(text)
      setInputCursor(cursor)
      setMentionOpen(false)
      setMentionQuery('')
      requestAnimationFrame(() => composerRef.current?.focus())
    },
    [input, mentionStart, inputCursor],
  )

  const syncMentionFromInput = useCallback(
    (val: string, cursor: number) => {
      const active = findActiveMention(val, cursor, datasetOptions)
      if (active && datasetOptions.length) {
        setMentionOpen(true)
        setMentionStart(active.start)
        setMentionQuery(active.query)
        setMentionIndex(0)
      } else {
        setMentionOpen(false)
      }
    },
    [datasetOptions],
  )

  const send = (textOverride?: string) => {
    const text = (textOverride ?? input).trim()
    if (!text) {
      if (!textOverride) message.warning('请输入问题后再发送')
      return
    }
    const { datasetId: mentionDatasetId, message: apiText } = parseDatasetMention(text, datasetOptions)
    if (mentionDatasetId && !apiText.trim()) {
      message.warning('已选择数据集，请输入具体问题')
      return
    }
    if (!apiText.trim()) {
      if (!textOverride) message.warning('请输入问题后再发送')
      return
    }
    const live = curId != null ? convLiveCache.get(curId) : null
    if (live?.sending) return

    const run = async (cid: number) => {
      abortConvStream(cid)
      const abort = new AbortController()
      streamAbortCache.set(cid, abort)

      if (!textOverride) setInput('')
      const userId = Date.now()
      const assistantId = userId + 1

      const initialMessages = [
        ...(convLiveCache.get(cid)?.messages ?? []),
        { id: userId, role: 'user', content: text, created_at: new Date().toISOString() },
        {
          id: assistantId,
          role: 'assistant',
          content: '',
          thinking_content: null,
          web_refs: null,
          citation_refs: null,
          chunk_refs: null,
          answer_figures: null,
          harness_rounds: [],
          agent_steps: [],
          created_at: new Date().toISOString(),
        },
      ] as Msg[]

      stickToBottomRef.current = true
      patchConvLive(cid, {
        messages: initialMessages,
        sending: true,
        streamingId: assistantId,
        streamPhase: 'retrieval',
      })
      scrollToLatest('smooth')
      if (ragDebugOn && curIdRef.current === cid) setLastRagDebug(null)

      const appendAgentSteps = (ev: ChatStreamEvent) => {
        patchConvLive(cid, (prev) => ({
          messages: prev.messages.map((m) => {
            if (m.id !== assistantId) return m
            const steps = applyAgentStepEvent(m.agent_steps || [], ev, mcpUi)
            return { ...m, agent_steps: steps }
          }),
        }))
      }

      const upsertHarnessRound = (round: number, patch: Partial<HarnessRoundLog>) => {
        patchConvLive(cid, (prev) => ({
          messages: prev.messages.map((m) => {
            if (m.id !== assistantId) return m
            const rounds = [...(m.harness_rounds || [])]
            const idx = rounds.findIndex((r) => r.round === round)
            const base: HarnessRoundLog =
              idx >= 0
                ? { ...rounds[idx], ...patch }
                : { round, status: 'searching', ...patch }
            if (idx >= 0) rounds[idx] = base
            else rounds.push(base)
            rounds.sort((a, b) => a.round - b.round)
            return { ...m, harness_rounds: rounds }
          }),
        }))
      }

      const handleHarnessEvent = (ev: ChatStreamEvent) => {
        if (ev.type === 'harness_round_start') {
          patchConvLive(cid, { streamPhase: 'retrieval' })
          upsertHarnessRound(ev.round, {
            round: ev.round,
            max_rounds: ev.max_rounds,
            retrieval_top_k: ev.retrieval_top_k,
            status: 'searching',
          })
          return
        }
        if (ev.type === 'harness_round_retrieval') {
          patchConvLive(cid, { streamPhase: 'judging' })
          upsertHarnessRound(ev.round, {
            max_rounds: ev.max_rounds,
            retrieval_top_k: ev.retrieval_top_k,
            hit_count: ev.hit_count,
            engine: ev.engine,
            titles: ev.titles,
            status: 'retrieved',
          })
          return
        }
        if (ev.type === 'hit_curation_start') {
          patchConvLive(cid, { streamPhase: 'curation' })
          return
        }
        if (ev.type === 'hit_curation_done') {
          patchConvLive(cid, { streamPhase: 'judging' })
          return
        }
        if (ev.type === 'harness_think_start') {
          patchConvLive(cid, { streamPhase: 'thinking' })
          upsertHarnessRound(ev.round, { max_rounds: ev.max_rounds, status: 'thinking' })
          return
        }
        if (ev.type === 'harness_judge_start') {
          patchConvLive(cid, {
            streamPhase: ev.mode === 'stream_answer' ? 'answering' : 'judging',
          })
          upsertHarnessRound(ev.round, { max_rounds: ev.max_rounds, status: 'judging' })
          return
        }
        if (ev.type === 'answer_generation_start' || ev.type === 'answer_generation_progress') {
          patchConvLive(cid, { streamPhase: 'answering' })
          return
        }
        if (ev.type === 'harness_round_result') {
          upsertHarnessRound(ev.round, {
            max_rounds: ev.max_rounds,
            found: ev.found,
            hit_count: ev.hit_count,
            refs_count: ev.refs_count,
            action: ev.action,
            status: 'done',
          })
          if (!ev.found) patchConvLive(cid, { streamPhase: 'retrieval' })
        }
      }
      try {
        await streamChatMessage(
          cid,
          {
            content: apiText,
            scope: kbScope,
            ...(mentionDatasetId ? { dataset_id: mentionDatasetId } : {}),
            deep_think: deepThink,
            web_search: webSearch,
          },
          (ev) => {
            handleHarnessEvent(ev)
            appendAgentSteps(ev)
            if (ev.type === 'user_saved') {
              const saved = ev.message as Msg
              patchConvLive(cid, (prev) => ({
                messages: prev.messages.map((m) =>
                  m.id === userId ? { ...m, id: saved.id } : m,
                ),
              }))
            } else if (ev.type === 'thinking_start') {
              if (ev.purpose !== 'answer_prep') {
                patchConvLive(cid, { streamPhase: 'thinking' })
              }
            } else if (ev.type === 'thinking_delta' || ev.type === 'thinking') {
              patchConvLive(cid, (prev) => ({
                streamPhase: prev.streamPhase === 'answering' ? 'answering' : 'thinking',
                messages: prev.messages.map((m) =>
                  m.id === assistantId
                    ? { ...m, thinking_content: (m.thinking_content || '') + ev.content }
                    : m,
                ),
              }))
            } else if (ev.type === 'thinking_end' || ev.type === 'harness_think_end') {
              patchConvLive(cid, {
                streamPhase: ev.type === 'harness_think_end' ? 'judging' : 'answering',
              })
            } else if (ev.type === 'delta') {
              patchConvLive(cid, (prev) => ({
                streamPhase: 'answering',
                messages: prev.messages.map((m) =>
                  m.id === assistantId ? { ...m, content: m.content + ev.content } : m,
                ),
              }))
            } else if (ev.type === 'answer_reset') {
              patchConvLive(cid, (prev) => ({
                streamPhase: 'answering',
                messages: prev.messages.map((m) =>
                  m.id === assistantId ? { ...m, content: ev.content } : m,
                ),
              }))
            } else if (ev.type === 'rag_done') {
              const rawRefs = (ev as { refs?: DocRef[] }).refs
              const rawCites = (ev as { citation_refs?: CitationRef[] }).citation_refs
              if (rawRefs?.length || rawCites?.length) {
                patchConvLive(cid, (prev) => ({
                  messages: prev.messages.map((m) =>
                    m.id === assistantId
                      ? {
                          ...m,
                          ...(rawRefs?.length ? { chunk_refs: rawRefs } : {}),
                          ...(rawCites?.length ? { citation_refs: rawCites } : {}),
                        }
                      : m,
                  ),
                }))
              }
            } else if (ev.type === 'done') {
              const saved = ev.message as Msg
              if (ev.rag_debug && curIdRef.current === cid) {
                setLastRagDebug(ev.rag_debug as RagDebugPayload)
              }
              patchConvLive(cid, (prev) => ({
                messages: prev.messages.map((m) =>
                  m.id === assistantId
                    ? {
                        ...saved,
                        qa_case_label: m.qa_case_label ?? saved.qa_case_label ?? '',
                        qa_gold_item_ids: m.qa_gold_item_ids ?? saved.qa_gold_item_ids ?? [],
                        harness_rounds: m.harness_rounds ?? saved.harness_rounds,
                        agent_steps: m.agent_steps ?? saved.agent_steps,
                      }
                    : m,
                ),
              }))
              void loadConvs(1, false)
            } else if (ev.type === 'error') {
              if (curIdRef.current === cid) message.error(ev.message)
              patchConvLive(cid, (prev) => ({
                messages: prev.messages.filter((m) => m.id !== assistantId),
              }))
            }
          },
          abort.signal,
        )
      } catch (e) {
        if (abort.signal.aborted) return
        if (curIdRef.current === cid) {
          message.error(e instanceof Error ? e.message : '发送失败')
        }
        patchConvLive(cid, (prev) => ({
          messages: prev.messages.filter((m) => m.id !== assistantId && m.id !== userId),
        }))
      } finally {
        streamAbortCache.delete(cid)
        patchConvLive(cid, {
          sending: false,
          streamingId: null,
          streamPhase: 'done',
        })
      }
    }

    if (curId) void run(curId)
    else {
      api.post<Conv>('/public/chat/conversations').then((r) => {
        convLiveCache.set(r.data.id, emptyConvLive())
        setActiveConvId(r.data.id)
        prependConv(r.data)
        void run(r.data.id)
      })
    }
  }

  const showInitialLoading = loading && messages.length === 0
  const showEmpty = !showInitialLoading && messages.length === 0

  const aside = (
    <WorkspaceAside
      title="历史对话"
      listRef={asideListRef}
      action={
        <Button type="primary" block icon={<PlusOutlined />} onClick={newChat}>
          新对话
        </Button>
      }
    >
      {convs.length === 0 ? (
        <div className="conv-empty">暂无对话，点击上方开始</div>
      ) : (
        <>
          {convs.map((c) => (
            <div
              key={c.id}
              className={`conv-item-wrap${curId === c.id ? ' conv-item-wrap-active' : ''}`}
            >
              <button
                type="button"
                className="conv-item"
                onClick={() => selectConv(c.id)}
              >
                <span className="conv-item-icon">
                  <CommentOutlined />
                </span>
                <span className="conv-item-body">
                  <span className="conv-item-title">{c.title || '新对话'}</span>
                  <span className="conv-item-time">{formatDate(c.updated_at)}</span>
                </span>
              </button>
              <Popconfirm
                title="删除此对话？"
                description="删除后无法恢复"
                okText="删除"
                cancelText="取消"
                okButtonProps={{ danger: true }}
                onConfirm={() => deleteConv(c.id)}
              >
                <Button
                  type="text"
                  size="small"
                  danger
                  className="conv-item-delete"
                  icon={<DeleteOutlined />}
                  aria-label="删除对话"
                  onClick={(e) => e.stopPropagation()}
                />
              </Popconfirm>
            </div>
          ))}
          {hasMoreConvs ? (
            <div ref={convLoadMoreRef} className="conv-list-hint">
              {convLoadingMore ? <Spin size="small" /> : '向下滚动加载更多'}
            </div>
          ) : null}
        </>
      )}
    </WorkspaceAside>
  )

  const focusComposer = () => {
    composerRef.current?.focus()
  }

  const sendingHint = [
    streamPhase === 'retrieval' ? '检索知识库' : null,
    streamPhase === 'curation' ? '整理文档' : null,
    streamPhase === 'judging' ? '判定依据' : null,
    streamPhase === 'thinking' ? '深度思考' : null,
    streamPhase === 'answering' ? '生成回答' : null,
    webSearch ? '联网搜索' : null,
    deepThink && streamPhase === 'done' ? '深度思考' : null,
    `查阅${scopeLabel}`,
  ]
    .filter(Boolean)
    .join(' · ')

  const footer = (
    <>
      <div className="chat-composer-tools">
        <button
          type="button"
          className={`chat-tool-btn${deepThink ? ' is-on' : ''}`}
          aria-pressed={deepThink}
          disabled={sending}
          onClick={() => {
            setDeepThink((v) => {
              const next = !v
              void persistChatComposerPrefs({ deepThink: next }).then((r) => {
                if (!r.persisted && getAdminToken() === null) {
                  message.warning('请先登录管理账号，深度思考偏好才会保存到服务器')
                }
              })
              return next
            })
          }}
        >
          <BulbOutlined />
          <span>深度思考</span>
        </button>
        <button
          type="button"
          className={`chat-tool-btn${webSearch ? ' is-on' : ''}`}
          aria-pressed={webSearch}
          disabled={sending}
          onClick={() => {
            setWebSearch((v) => {
              const next = !v
              void persistChatComposerPrefs({ webSearch: next }).then((r) => {
                if (!r.persisted && getAdminToken() === null) {
                  message.warning('请先登录管理账号，联网搜索偏好才会保存到服务器')
                }
              })
              return next
            })
          }}
          title="按需检索网页，补充知识库以外的实时信息"
        >
          <GlobalOutlined />
          <span>联网搜索</span>
        </button>
      </div>
      <div
      className="workspace-composer-inner chat-composer-inner"
      onMouseDown={(e) => {
        const el = e.target as HTMLElement
        if (el.closest('button')) return
        if (!el.closest('textarea') && !el.closest('.chat-dataset-mention-menu')) focusComposer()
      }}
    >
      <div className="chat-composer-main">
        {mentionOpen ? (
          <ul className="chat-dataset-mention-menu" role="listbox">
            {filteredMentionDatasets.length ? (
              filteredMentionDatasets.map((ds, idx) => (
                <li key={ds.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={idx === mentionIndex}
                    className={idx === mentionIndex ? 'is-active' : undefined}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pickDatasetMention(ds)}
                  >
                    @{ds.name}
                  </button>
                </li>
              ))
            ) : (
              <li className="chat-dataset-mention-empty">无匹配的数据集</li>
            )}
          </ul>
        ) : null}
        {activeDatasetLabel ? (
          <div className="chat-dataset-mention-hint">
            <Tag color="blue" style={{ margin: 0 }}>
              检索范围：{activeDatasetLabel}
            </Tag>
          </div>
        ) : null}
        <Input.TextArea
        ref={composerRef}
        classNames={{ textarea: 'chat-composer-input' }}
        autoSize={{ minRows: 1, maxRows: 4 }}
        value={input}
        onChange={(e) => {
          const val = e.target.value
          const cursor = e.target.selectionStart ?? val.length
          setInput(val)
          setInputCursor(cursor)
          syncMentionFromInput(val, cursor)
        }}
        onSelect={(e) => {
          const el = e.target as HTMLTextAreaElement
          const cursor = el.selectionStart ?? input.length
          setInputCursor(cursor)
          syncMentionFromInput(input, cursor)
        }}
        onKeyDown={(e) => {
          if (mentionOpen && filteredMentionDatasets.length) {
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              setMentionIndex((i) => Math.min(i + 1, filteredMentionDatasets.length - 1))
              return
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault()
              setMentionIndex((i) => Math.max(i - 1, 0))
              return
            }
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              pickDatasetMention(filteredMentionDatasets[mentionIndex] ?? filteredMentionDatasets[0])
              return
            }
            if (e.key === 'Escape') {
              e.preventDefault()
              setMentionOpen(false)
              return
            }
          }
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            if (!sending) send()
          }
        }}
        placeholder={
          datasetOptions.length
            ? '输入问题，@ 引用已发布数据集；Enter 发送（Shift+Enter 换行）'
            : '输入问题，Enter 发送（Shift+Enter 换行）'
        }
      />
      </div>
      <Button type="primary" icon={<SendOutlined />} loading={sending} onClick={() => send()}>
        发送
      </Button>
    </div>
    </>
  )

  return (
    <WorkspaceShell
      title={curConv?.title || 'AI 对话'}
      description={
        <div className="chat-page-head">
          <span className="chat-page-head-desc">
            基于 <strong>{scopeLabel}</strong> 检索作答；输入 <strong>@</strong>{' '}
            引用已发布数据集，或用【文档名】指定单篇文档
          </span>
          <span className="chat-page-head-right">
            {ragDebugOn ? <Tag color="orange">RAG 调试已开启</Tag> : null}
            <Segmented
              className="chat-kb-segmented"
              value={kbScope}
              options={SCOPE_OPTIONS}
              onChange={(v) => setKbScope(v as KnowledgeScope)}
              disabled={sending}
            />
          </span>
        </div>
      }
      aside={aside}
      footer={footer}
    >
      {esStatus && esStatus.enabled && !esStatus.connected ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Elasticsearch 未连接"
          description="无法从知识库读取切片数据，对话检索将不可用。请启动 ES 服务并检查 ES_URL 配置后重试。"
        />
      ) : null}
      {showInitialLoading && <PageLoading />}
      {!showInitialLoading && showEmpty && (
        <div className="chat-empty">
          <div className="chat-empty-icon">
            <RobotOutlined />
          </div>
          <h3>开始与 {scopeLabel} 对话</h3>
          <p>
            输入具体业务问题（如制度条款、流程步骤），系统会从知识库中查找相关内容后组织回答。
          </p>
          <div className="chat-suggestions">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                className="chat-suggestion"
                onClick={() => send(s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
      {!showInitialLoading && !showEmpty && (
        <div className="chat-messages">
          {messages.map((m) => {
            const isUser = m.role === 'user'
            const refs = !isUser && m.chunk_refs?.length ? m.chunk_refs : []
            const answerFigures =
              !isUser && m.answer_figures?.length ? m.answer_figures : []
            const agentSteps = !isUser ? m.agent_steps || [] : []
            const webRefs = !isUser && m.web_refs?.length ? m.web_refs : []
            const citationRefs =
              !isUser && m.citation_refs?.length ? m.citation_refs : []
            const isStreamingMsg = !isUser && m.id === streamingId
            const showAgentSteps =
              agentSteps.length > 0 || (isStreamingMsg && streamPhase !== 'done')
            const agentIntro = shouldShowAgentIntro(agentSteps)
              ? '以下是内部资料搜索结果：'
              : undefined
            const agentStepLoading = isStreamingMsg && hasRunningAgentStep(agentSteps)
            const showAnswerActions =
              !isUser &&
              !isStreamingMsg &&
              Boolean(m.content?.trim()) &&
              isPersistedChatMessageId(m.id)
            const showAnswerBubble =
              Boolean(m.content) ||
              (isStreamingMsg && streamPhase !== 'done' && !agentStepLoading)
            const streamPendingLabel =
              streamPhase === 'curation'
                ? '正在整理相关文档…'
                : streamPhase === 'judging'
                  ? '正在判断是否找到依据…'
                  : streamPhase === 'thinking'
                    ? '深度思考中…'
                    : streamPhase === 'retrieval'
                      ? '正在检索知识库…'
                      : '正在整理回答…'
            return (
              <div key={m.id} className={`chat-msg${isUser ? ' chat-msg-user' : ''}`}>
                <div
                  className={`chat-msg-avatar ${
                    isUser ? 'chat-msg-avatar-user' : 'chat-msg-avatar-assistant'
                  }`}
                >
                  {isUser ? <UserOutlined /> : <RobotOutlined />}
                </div>
                <div className="chat-msg-main">
                  {showAgentSteps ? (
                    <ChatAgentSteps
                      steps={agentSteps}
                      intro={agentIntro}
                      streaming={isStreamingMsg && streamPhase !== 'done'}
                      docRefs={refs}
                      defaultScope={kbScope}
                      onPreview={openSourcePreview}
                    />
                  ) : null}
                  {showAnswerBubble ? (
                    <div
                      className={`chat-msg-bubble ${
                        isUser ? 'chat-msg-bubble-user' : 'chat-msg-bubble-assistant'
                      }${isStreamingMsg && streamPhase === 'answering' ? ' chat-msg-bubble-streaming' : ''}${
                        isStreamingMsg && !m.content && !agentStepLoading ? ' chat-msg-loading' : ''
                      }`}
                    >
                      {m.content ? (
                        <>
                          {isUser ? (
                            m.content
                          ) : answerFigures.length > 0 ? (
                            <ChatAnswerFigures
                              content={m.content}
                              figures={answerFigures}
                              defaultScope={kbScope}
                            />
                          ) : citationRefs.length > 0 && hasCitationMarkers(m.content) ? (
                            <ChatAnswerWithCitations
                              content={m.content}
                              citationRefs={citationRefs}
                              defaultScope={kbScope}
                              onPreview={openSourcePreview}
                            />
                          ) : (
                            <ChatMarkdown content={m.content} />
                          )}
                          {showAnswerActions ? (
                            <ChatMessageActions
                              messageId={m.id}
                              content={m.content}
                              caseLabel={(m.qa_case_label === 'good' || m.qa_case_label === 'bad'
                                ? m.qa_case_label
                                : '') as QaCaseLabel}
                              onCaseLabelChange={(label) => setMessageCaseLabel(m.id, label)}
                            />
                          ) : null}
                        </>
                      ) : isStreamingMsg ? (
                        <span className="chat-msg-bubble-pending">{streamPendingLabel}</span>
                      ) : null}
                    </div>
                  ) : null}
                  {refs.length > 0 ? (
                    <ChatSources
                      refs={refs}
                      defaultScope={kbScope}
                      onPreview={openSourcePreview}
                      messageId={showAnswerActions ? m.id : undefined}
                      goldItemIds={m.qa_gold_item_ids || []}
                      onGoldChange={
                        showAnswerActions
                          ? (ids) => setMessageGoldIds(m.id, ids)
                          : undefined
                      }
                    />
                  ) : null}
                  {webRefs.length > 0 ? <ChatWebSources refs={webRefs} /> : null}
                </div>
              </div>
            )
          })}
          {sending && streamingId == null && (
            <div className="chat-msg chat-msg-loading">
              <div className="chat-msg-avatar chat-msg-avatar-assistant">
                <RobotOutlined />
              </div>
              <div className="chat-msg-main">
                <div className="chat-msg-bubble chat-msg-bubble-assistant">
                  <Spin size="small" /> 正在{sendingHint}…
                </div>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
          {ragDebugOn ? (
            <ChatRagDebugPanel debug={lastRagDebug} pending={sending || streamingId != null} />
          ) : null}
        </div>
      )}
      <KnowledgeFilePreviewDrawer
        open={previewTarget != null}
        itemId={previewTarget?.itemId ?? null}
        scopeParams={previewScopeParams}
        onClose={() => setPreviewTarget(null)}
      />
    </WorkspaceShell>
  )
}
